import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRandomContent, recordProgress, awardBonus, Content } from '../services/api';
import { speak } from '../services/audio';
import './Games.css';

const ListeningChallenge: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lessonId = searchParams.get('lessonId') || undefined;
  const customSetId = searchParams.get('customSetId') || undefined;

  const [items, setItems] = useState<Content[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [options, setOptions] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [correct, setCorrect] = useState<boolean | null>(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadItems = async () => {
      try {
        const data = await getRandomContent(10, lessonId, customSetId);
        setItems(data);
        if (data.length > 0) {
          generateOptions(data, 0);
        }
      } catch (err) {
        console.error('Failed to load content:', err);
      } finally {
        setLoading(false);
      }
    };
    loadItems();
  }, [lessonId, customSetId]);

  const generateOptions = (allItems: Content[], index: number) => {
    const correctAnswer = allItems[index].english;
    const wrongAnswers = allItems
      .filter((_, i) => i !== index)
      .map(item => item.english)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);
    
    const allOptions = [correctAnswer, ...wrongAnswers].sort(() => Math.random() - 0.5);
    setOptions(allOptions);
    setTimeout(() => speak(allItems[index].english, 0.8), 500);
  };

  const handleSelect = async (answer: string) => {
    if (selected) return;
    
    const currentItem = items[currentIndex];
    const isCorrect = answer === currentItem.english;
    
    setSelected(answer);
    setCorrect(isCorrect);
    
    await recordProgress(currentItem.id, isCorrect);
    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));

    setTimeout(() => {
      if (currentIndex < items.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setSelected(null);
        setCorrect(null);
        generateOptions(items, currentIndex + 1);
      } else {
        handleComplete();
      }
    }, 1500);
  };

  const handleComplete = async () => {
    if (score.correct === items.length) {
      await awardBonus(25, 'perfect_listening');
    }
    setCompleted(true);
  };

  if (loading) return <div className="loading">Ładowanie...</div>;
  if (items.length < 4) {
    return (
      <div className="game-error">
        <h2>Za mało słówek!</h2>
        <p>Potrzebujesz minimum 4 słówek do tej gry.</p>
        <button className="btn btn-primary" onClick={() => navigate('/games')}>Wróć</button>
      </div>
    );
  }

  const currentItem = items[currentIndex];
  if (!currentItem) return <div className="loading">Ładowanie...</div>;

  if (completed) {
    const isPerfect = score.correct === items.length;
    return (
      <div className="game-complete fade-in">
        <div className="complete-icon">{isPerfect ? '🏆' : '🎉'}</div>
        <h1>{isPerfect ? 'Perfekcyjnie!' : 'Świetnie!'}</h1>
        <p>Wynik: {score.correct} / {score.total}</p>
        {isPerfect && <div className="bonus-badge bounce">+25 bonus!</div>}
        <button className="btn btn-primary btn-large" onClick={() => window.location.reload()}>
          Zagraj ponownie
        </button>
      </div>
    );
  }

  return (
    <div className="game-container fade-in">
      <div className="game-header">
        <h2>👂 Słuchanie</h2>
        <div className="game-progress">{currentIndex + 1} / {items.length}</div>
      </div>

      <div className="game-score">✅ {score.correct} / {score.total}</div>

      <div className="listening-card">
        <button className="big-speak-btn" onClick={() => speak(currentItem.english, 0.8)}>
          🔊
        </button>
        <p>Kliknij, aby posłuchać</p>
      </div>

      <p className="quiz-instruction">Które słowo usłyszałeś?</p>

      <div className="options-grid">
        {options.map((option, i) => (
          <button
            key={i}
            className={`option-btn ${selected === option ? (correct ? 'correct' : 'wrong') : ''} 
              ${selected && option === currentItem.english ? 'correct' : ''}`}
            onClick={() => handleSelect(option)}
            disabled={!!selected}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ListeningChallenge;

